import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { MessageSquare, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";
import { useMutation } from "@tanstack/react-query";
import { LoginRequestSchema } from "@/lib/api";
import { HTTPError } from "ky";

export const Route = createFileRoute("/auth/login")({
  beforeLoad: () => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search: Record<string, unknown>) => {
    return {
      redirectTo: (search.redirectTo as string) || "/dashboard",
    };
  },
  component: Login,
});

function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { redirectTo } = useSearch({
    from: "/auth/login",
    select: (search) => ({
      redirectTo: search.redirectTo || "/dashboard",
    }),
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // Create login mutation using React Query
  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: () => {
      // Redirect to the intended page or dashboard
      navigate({ to: redirectTo });
    },
    onError: (error: Error) => {
      if (error instanceof HTTPError) {
        // Handle HTTP errors (e.g., 401 Unauthorized)
        if (error.response.status === 401) {
          setError("Invalid email or password");
        } else {
          setError("An error occurred during login. Please try again.");
        }
      } else {
        setError(error.message || "An unexpected error occurred");
      }
      console.error("Login error:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      // Validate form data with Zod schema
      const loginData = LoginRequestSchema.parse({ email, password });

      // Execute login mutation
      loginMutation.mutate(loginData);
    } catch (err: any) {
      // Handle validation errors
      if (err.errors) {
        setError(err.errors.map((e: any) => e.message).join(", "));
      } else {
        setError("Please check your input and try again.");
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 relative max-w-lg mx-auto">
      {/* Background elements */}
      <div className="absolute top-[20%] right-[10%] w-64 h-64 bg-blue-300 rounded-full blur-3xl opacity-20 animate-float"></div>
      <div
        className="absolute bottom-[10%] left-[15%] w-72 h-72 bg-purple-300 rounded-full blur-3xl opacity-10 animate-float"
        style={{ animationDelay: "2s" }}
      ></div>

      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shadow-md">
              <MessageSquare className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">
            aiEmailAgent<span className="text-blue-600">.</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            The AI-powered email assistant for sales teams
          </p>
        </div>

        <div className="glass rounded-xl p-8 shadow-lg border">
          <div className="space-y-2 mb-6">
            <h2 className="text-2xl font-bold">Sign In</h2>
            <p className="text-muted-foreground text-sm">
              Enter your credentials to access your account
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive mb-4 animate-scale">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium leading-none"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                required
                disabled={loginMutation.isPending}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="text-sm font-medium leading-none"
                >
                  Password
                </label>
                <a
                  href="/auth/forgot-password"
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Forgot password?
                </a>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loginMutation.isPending}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground py-2.5 mt-2 hover:shadow-md transition-all duration-200 rounded-lg text-sm font-medium"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <span className="flex items-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            Don't have an account?{" "}
            <Link
              to="/auth/register"
              className="font-medium text-primary hover:underline"
              search={{ redirectTo: redirectTo }}
            >
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
